import React, { useState, useRef, useCallback } from "react";
import { Button, Space, Tooltip } from "antd";
import { DownloadOutlined, CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";
import Logger from "@/utility/logger";

function FileAttachment({
  selectedProject,
  fileInputRef,
  isDragOver,
  setIsDragOver,
  uploadedFiles,
  setUploadedFiles,
  uploadingFiles,
  setUploadingFiles,
  uploadDocument,
  deleteDocument,
  deleteTag,
  regenerateDialogContent,
  type,
  id = "",
  logAuditAction = null,
  reportTemplateProjectId = null,
  sectionDisplayName = "",
}) {
  const maxFileSize = 1024 * 1024 * 10; // 10MB default

  const allowedFileTypes = [
    "text/plain",
    "text/csv",
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    // "application/json",
    "image/jpeg",
    "image/png",
    "text/markdown",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  ];

  const readFileContent = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = (e) => reject(e);
      reader.readAsText(file);
    });
  }, []);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleFilesUpload = (files) => {
    setUploadedFiles(files);
  };

  const handleRemoveFile = async (fileId: string) => {
    setUploadedFiles((prev) => prev.filter((file) => file.id !== fileId));
    const file = uploadedFiles.find((f) => f.id === fileId);
    await deleteDocument(file.doc_id);
    await deleteTag(file.tag_id);
    // Audit trail: log DELETE action for file attachment removal
    if (logAuditAction && reportTemplateProjectId) {
      logAuditAction(reportTemplateProjectId, "DELETE", `File Removed: ${file.name}${sectionDisplayName ? ` (${sectionDisplayName})` : ""}`);
    }
  };

  const processFiles = useCallback(
    async (files: FileList | File[]) => {
      if (type === "regenerate") {
        const fileArray = Array.from(files);
        const newUploadedFiles = [];

        for (const file of fileArray) {
          const fileId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

          // Validate file type
          if (!allowedFileTypes.includes(file.type)) {
            const errorFile = {
              id: fileId,
              file,
              name: file.name,
              size: file.size,
              type: file.type,
              uploadProgress: 0,
              error: `File type ${file.type} is not allowed`,
            };
            newUploadedFiles.push(errorFile);
            continue;
          }

          // Validate file size
          if (file.size > maxFileSize) {
            const errorFile = {
              id: fileId,
              file,
              name: file.name,
              size: file.size,
              type: file.type,
              uploadProgress: 0,
              error: `File size exceeds ${formatFileSize(maxFileSize)} limit`,
            };
            newUploadedFiles.push(errorFile);
            continue;
          }

          // Create initial file object with explicit typing
          const uploadedFile = {
            id: fileId,
            sg_id: "",
            doc_id: "",
            tag_id: [],
            file,
            name: file.name,
            size: file.size,
            type: file.type,
            uploadProgress: 0,
            content: undefined,
            error: undefined,
          };

          newUploadedFiles.push(uploadedFile);
          setUploadingFiles((prev) => new Set(Array.from(prev).concat(fileId)));

          // Show the file immediately in the UI
          handleFilesUpload([...uploadedFiles, ...newUploadedFiles]);

          // Start processing the file
          try {
            // Update progress to show we're starting
            uploadedFile.uploadProgress = 10;
            handleFilesUpload([...uploadedFiles, ...newUploadedFiles]);

            uploadedFile.sg_id = regenerateDialogContent.section.id;

            // Upload the document with progress updates
            uploadedFile.uploadProgress = 30;
            handleFilesUpload([...uploadedFiles, ...newUploadedFiles]);

            const fileContent = await uploadDocument(selectedProject.id, [], file);
            uploadedFile.doc_id = fileContent.id;
            Logger.debug(`FileAttachment.tsx: File content uploaded for ${file.name}:`, fileContent?.id);
            if (fileContent?.tags && fileContent?.tags.length > 0) {
              const ids = fileContent.tags.map((tagObject) => tagObject.id);
              uploadedFile.tag_id.push(...ids);
            }

            // Update progress after upload
            uploadedFile.uploadProgress = 70;
            handleFilesUpload([...uploadedFiles, ...newUploadedFiles]);

            // Read file content based on type
            if (file.type.startsWith("text/") || file.type === "application/json") {
              uploadedFile.content = await readFileContent(file);
            }

            // Audit trail: log CREATE action for file attachment upload
            if (logAuditAction && reportTemplateProjectId) {
              logAuditAction(reportTemplateProjectId, "CREATE", `File Attached: ${file.name}${sectionDisplayName ? ` (${sectionDisplayName})` : ""}`);
            }

            // Complete
            uploadedFile.uploadProgress = 100;
            setUploadingFiles((prev) => {
              const newSet = new Set(Array.from(prev));
              newSet.delete(fileId);
              return newSet;
            });

            // Final update
            handleFilesUpload([...uploadedFiles, ...newUploadedFiles]);
          } catch (error) {
            Logger.error(`FileAttachment.tsx: Error processing file ${file.name}:`, error);
            uploadedFile.error = "Failed to process file";
            uploadedFile.uploadProgress = 0;
            setUploadingFiles((prev) => {
              const newSet = new Set(Array.from(prev));
              newSet.delete(fileId);
              return newSet;
            });

            // Update with error
            handleFilesUpload([...uploadedFiles, ...newUploadedFiles]);
          }
        }
      } else if (type === "agent") {
        const fileArray = Array.from(files);
        const newUploadedFiles = [];

        for (const file of fileArray) {
          const fileId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

          // Validate file type
          if (!allowedFileTypes.includes(file.type)) {
            const errorFile = {
              id: fileId,
              file,
              name: file.name,
              size: file.size,
              type: file.type,
              uploadProgress: 0,
              error: `File type ${file.type} is not allowed`,
            };
            newUploadedFiles.push(errorFile);
            continue;
          }

          // Validate file size
          if (file.size > maxFileSize) {
            const errorFile = {
              id: fileId,
              file,
              name: file.name,
              size: file.size,
              type: file.type,
              uploadProgress: 0,
              error: `File size exceeds ${formatFileSize(maxFileSize)} limit`,
            };
            newUploadedFiles.push(errorFile);
            continue;
          }

          // Create initial file object with explicit typing
          const uploadedFile = {
            id: fileId,
            sg_id: "",
            doc_id: "",
            tag_id: "",
            file,
            name: file.name,
            size: file.size,
            type: file.type,
            uploadProgress: 0,
            content: undefined,
            error: undefined,
          };

          newUploadedFiles.push(uploadedFile);
          handleFilesUpload([...uploadedFiles, ...newUploadedFiles]);
          uploadedFile.uploadProgress = 100;
          uploadedFile.sg_id = id;
        }
      }
    },
    [allowedFileTypes, maxFileSize, uploadedFiles, handleFilesUpload, readFileContent]
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        processFiles(files);
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [processFiles]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        processFiles(files);
      }
    },
    [processFiles]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const getFileIcon = (type: string) => {
    if (type.startsWith("text/")) {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <polyline
            points="14,2 14,8 20,8"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <polyline points="10,9 9,9 8,9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
    } else if (type === "application/pdf") {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <polyline
            points="14,2 14,8 20,8"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path d="M9 13h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <path d="M9 17h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
    } else {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <polyline
            points="14,2 14,8 20,8"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    }
  };

  return (
    <>
      <div className="file-attachment-section">
        <label className="file-attachment-section-label">
          <span className="file-attachment-section-title">Upload Files</span>
          <span className="file-attachment-section-description">
            Upload additional documents to reference during regeneration
          </span>
        </label>

        <div
          className={`file-attachment-upload-area ${isDragOver ? "drag-over" : ""}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => handleFileSelect}
        >
          <div className="file-attachment-upload-content">
            <div className="file-attachment-upload-icon">
              <DownloadOutlined style={{ fontSize: "1.5rem" }} />
            </div>
            <div className="file-attachment-upload-text">
              <span className="file-attachment-upload-primary">Click to upload or drag and drop</span>
              <span className="file-attachment-upload-secondary">
                PDF, DOCX, CSV, JPG, PNG, XLSX, MD, PPTX, TXT up to {formatFileSize(maxFileSize)}
              </span>
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={allowedFileTypes.join(",")}
            onChange={handleFileSelect}
            className="file-attachment-upload-input"
          />
        </div>

        {uploadedFiles.length > 0 && (
          <div className="file-attachment-uploaded-files">
            {uploadedFiles
              .filter((file) =>
                type === "regenerate"
                  ? file.sg_id === regenerateDialogContent.section.id
                  : type === "agent"
                  ? file.sg_id === id
                  : false
              )
              .map((file) => (
                <div key={file.id} className="file-attachment-uploaded-file">
                  <div className="file-attachment-uploaded-file-info">
                    <div className="file-attachment-uploaded-file-icon">{getFileIcon(file.type)}</div>
                    <div className="file-attachment-uploaded-file-details">
                      <div className="file-attachment-uploaded-file-name">{file.name}</div>
                      <div className="file-attachment-uploaded-file-meta">
                        {formatFileSize(file.size)} • {file.type}
                      </div>
                      {file.error && <div className="file-attachment-uploaded-file-error">{file.error}</div>}
                    </div>
                  </div>

                  <div className="file-attachment-uploaded-file-actions">
                    {uploadingFiles.has(file.id) && (
                      <div className="file-attachment-upload-progress">
                        <div
                          className="file-attachment-upload-progress-bar"
                          style={{ width: `${file.uploadProgress}%` }}
                        />
                      </div>
                    )}

                    {!uploadingFiles.has(file.id) && !file.error && (
                      <div className="file-attachment-uploaded-file-status">
                        <CheckOutlined />
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={() => handleRemoveFile(file.id)}
                      className="file-attachment-uploaded-file-remove"
                      aria-label={`Remove ${file.name}`}
                    >
                      <CloseOutlined style={{ color: "red" }} />
                    </button>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </>
  );
}

export default FileAttachment;
